import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowRight, ArrowUpRight, MessageCircle, Instagram, Truck, Package, ShieldCheck, MousePointerClick } from "lucide-react";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import ChapterHeading from "@/components/ChapterHeading";
import ProductCard from "@/components/ProductCard";
import { getProducts } from "@/lib/api";
import { waGeneral, waEncomenda, waProntaEntrega, INSTAGRAM_URL, INSTAGRAM_HANDLE } from "@/lib/whatsapp";
import { useSite } from "@/context/SiteContext";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const CountUp = ({ to }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, { duration: 1.8, ease: "easeOut", onUpdate: (v) => setVal(Math.round(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref}>+{val.toLocaleString("pt-BR")}</span>;
};

const STEPS = [
  { icon: MousePointerClick, title: "Escolha seu modelo", desc: "Encontre seu boné ou peça favorita." },
  { icon: MessageCircle, title: "Clique em Tenho Interesse", desc: "Você será direcionado para o WhatsApp." },
  { icon: ShieldCheck, title: "Fale com a DM Imports", desc: "Nossa equipe confirma valor, disponibilidade e condições." },
  { icon: Truck, title: "Receba seu produto", desc: "Combine pagamento e envio diretamente com a equipe." },
];

const Hero = () => {
  const site = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <motion.img
          src={site.images.heroBg}
          alt="Coleção de bonés importados DM Imports"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: EASE }}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/70 to-obsidian/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/60" />
      </motion.div>

      <div className="mx-auto grid w-full max-w-7xl gap-12 px-5 pb-24 pt-36 sm:px-8 lg:grid-cols-[1.3fr_1fr] lg:items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="mb-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-silver"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-silver/60" />
            DM Imports — Bonés | Roupas Premium
          </motion.p>

          <h1 data-testid="hero-title" className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-offwhite sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.35}>{site.texts.heroL1}</MaskedLine>
            <MaskedLine delay={0.5}>
              {site.texts.heroL2Prefix} <span className="text-stroke">{site.texts.heroL2Accent}</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            data-testid="hero-subtitle"
            className="mt-6 max-w-md text-base text-zinc-400 md:text-lg"
          >
            {site.texts.heroSubtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-5 text-xs uppercase tracking-[0.2em] text-zinc-500"
            data-testid="hero-proof"
          >
            {site.texts.heroProof}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Link
              to="/catalogo"
              data-testid="hero-catalog-button"
              className="group flex items-center justify-center gap-3 rounded-full bg-offwhite px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-transform duration-300 hover:scale-[1.03]"
            >
              Ver Catálogo
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={waGeneral()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="flex items-center justify-center gap-3 rounded-full border border-silver/40 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-offwhite transition-colors duration-300 hover:bg-offwhite hover:text-obsidian"
            >
              <MessageCircle className="h-4 w-4" />
              Falar no WhatsApp
            </a>
          </motion.div>
        </div>

        <motion.div
          style={{ y: fgY }}
          initial={{ opacity: 0, y: 60, rotate: 6 }}
          animate={{ opacity: 1, y: 0, rotate: 3 }}
          transition={{ delay: 0.9, duration: 1.1, ease: EASE }}
          className="relative hidden lg:block"
        >
          <div className="overflow-hidden rounded-2xl border border-silver/25 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]">
            <img src={site.images.heroFg} alt="Cliente usando boné importado DM Imports" className="aspect-[4/5] w-full object-cover" />
          </div>
          <div className="absolute -bottom-5 -left-5 rounded-xl border border-white/10 bg-obsidian/80 px-5 py-4 backdrop-blur-xl">
            <p className="font-display text-xl font-extrabold text-offwhite">+1.000</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">clientes atendidos</p>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 sm:flex"
        aria-hidden="true"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Role</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="h-8 w-px bg-gradient-to-b from-silver/70 to-transparent" />
      </motion.div>
    </section>
  );
};

const Home = () => {
  const site = useSite();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    document.title = "DM IMPORTS | Bonés Importados e Roupas Premium";
    getProducts().then(setProducts).catch(() => {});
  }, []);

  const featured = products.filter((p) => p.destaque);
  const bones = products.filter((p) => p.categoria?.startsWith("Bonés")).slice(0, 8);
  const readyStock = products.filter((p) => p.disponibilidade === "pronta_entrega");

  const CATEGORIES = [
    { title: "Bonés Básicos", desc: "Modelos essenciais para o dia a dia.", cta: "Ver Básicos", to: "/catalogo?categoria=Bonés%20Básicos", img: site.images.catBones, id: "categoria-basicos" },
    { title: "Bonés Premium", desc: "Modelos premium selecionados.", cta: "Ver Premium", to: "/catalogo?categoria=Bonés%20Premium", img: site.images.catEncomendas, id: "categoria-premium" },
    { title: "Bonés Importados", desc: "Modelos importados selecionados.", cta: "Ver Importados", to: "/importados", img: site.images.catRoupas, id: "categoria-importados" },
  ];

  return (
    <>
      <Hero />
      <Marquee />

      {/* CATEGORIAS */}
      <section id="categorias" data-testid="categories-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <ChapterHeading num="01" eyebrow="Categorias" title="Encontre o seu estilo" testid="categories-heading" />
        <div className="grid gap-5 md:grid-cols-3">
          {CATEGORIES.map((cat, i) => (
            <Reveal key={cat.id} delay={i * 0.12}>
              <div data-testid={cat.id} className="group relative h-[420px] overflow-hidden rounded-2xl border border-white/10 sm:h-[480px]">
                <img src={cat.img} alt={`${cat.title} — DM Imports`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                  <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-offwhite sm:text-3xl">{cat.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400">{cat.desc}</p>
                  {cat.external ? (
                    <a href={cat.to} target="_blank" rel="noopener noreferrer" data-testid={`${cat.id}-button`} className="mt-5 inline-flex items-center gap-2 rounded-full bg-offwhite px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-obsidian transition-transform duration-300 hover:scale-[1.04]">
                      {cat.cta} <ArrowUpRight className="h-4 w-4" />
                    </a>
                  ) : (
                    <Link to={cat.to} data-testid={`${cat.id}-button`} className="mt-5 inline-flex items-center gap-2 rounded-full bg-offwhite px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-obsidian transition-transform duration-300 hover:scale-[1.04]">
                      {cat.cta} <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATÁLOGO EM DESTAQUE */}
      <section id="destaque" data-testid="catalog-preview-section" className="border-y border-white/10 bg-graphite py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <ChapterHeading num="02" eyebrow="Catálogo" title="Bonés em destaque" sub="Confira alguns dos modelos disponíveis na DM Imports." testid="catalog-preview-heading" />
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {bones.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <Link to="/catalogo" data-testid="catalog-preview-see-all" className="group inline-flex items-center gap-3 rounded-full border border-silver/40 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-offwhite transition-colors duration-300 hover:bg-offwhite hover:text-obsidian">
              Ver catálogo completo
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* DESTAQUES DM IMPORTS */}
      {featured.length > 0 && (
        <section data-testid="featured-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <ChapterHeading num="03" eyebrow="Seleção Premium" title="Destaques DM Imports" sub="Os modelos mais procurados. Consulte o valor direto no WhatsApp." testid="featured-heading" />
          <div className="space-y-16 sm:space-y-24">
            {featured.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} data-testid={`featured-item-${p.codigo}`}>
                <div className={`grid items-center gap-8 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <div className="group overflow-hidden rounded-2xl border border-white/10">
                    <img src={p.imagem} alt={`${p.nome} — destaque DM Imports`} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="lg:px-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-zinc-500">{p.marca} · {p.subcategoria}</p>
                    <h3 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-offwhite sm:text-3xl lg:text-4xl">{p.nome}</h3>
                    <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-400 sm:text-base">{p.descricao}</p>
                    <p className="mt-3 text-xs uppercase tracking-[0.2em] text-zinc-500">
                      Cor: {p.cor} · {p.disponibilidade === "pronta_entrega" ? "Pronta Entrega" : "Encomenda"}
                    </p>
                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                      <Link to={`/produto/${p.id}`} data-testid={`featured-view-${p.codigo}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-silver/40 px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.18em] text-offwhite transition-colors duration-300 hover:bg-offwhite hover:text-obsidian">
                        Ver modelo <ArrowRight className="h-4 w-4" />
                      </Link>
                      <a href={waGeneral()} target="_blank" rel="noopener noreferrer" data-testid={`featured-whatsapp-${p.codigo}`} className="inline-flex items-center justify-center gap-2 rounded-full bg-offwhite px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.18em] text-obsidian transition-transform duration-300 hover:scale-[1.03]">
                        <MessageCircle className="h-4 w-4" /> Chamar no WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* MARCAS */}
      <section id="marcas" data-testid="brands-section" className="border-y border-white/10 bg-graphite py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <ChapterHeading num="04" eyebrow="Curadoria" title="Marcas que fazem parte do nosso estilo" testid="brands-heading" />
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {site.brands.map((brand, i) => (
              <Reveal key={brand} delay={i * 0.08} className="group flex min-h-[140px] items-center justify-center bg-obsidian p-8 transition-colors duration-500 hover:bg-coal">
                <p data-testid={`brand-${brand.toLowerCase().replace(/[^a-z]/g, "-")}`} className="text-center font-display text-lg font-bold uppercase leading-snug tracking-wide text-zinc-400 transition-colors duration-500 group-hover:text-offwhite sm:text-xl">
                  {brand}
                </p>
              </Reveal>
            ))}
            <Reveal delay={0.35} className="group flex min-h-[140px] items-center justify-center bg-obsidian p-8 transition-colors duration-500 hover:bg-coal" style={((site.brands.length + 1) % 3) ? { gridColumn: `span ${3 - ((site.brands.length + 1) % 3) + 1} / span ${3 - ((site.brands.length + 1) % 3) + 1}` } : undefined}>
              <a href={waGeneral()} target="_blank" rel="noopener noreferrer" data-testid="brands-more-link" className="flex items-center gap-2 text-center font-display text-lg font-bold uppercase tracking-wide text-silver sm:text-xl">
                E outras <ArrowUpRight className="h-5 w-5" />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRONTA ENTREGA */}
      <section id="pronta-entrega" data-testid="ready-stock-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <ChapterHeading num="05" eyebrow="Envio imediato" title="Pronta Entrega" sub="Modelos disponíveis para envio imediato." testid="ready-stock-heading" />
          <Reveal className="shrink-0 lg:pb-16">
            <a href={waProntaEntrega()} target="_blank" rel="noopener noreferrer" data-testid="ready-stock-whatsapp-button" className="inline-flex items-center gap-3 rounded-full bg-offwhite px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-transform duration-300 hover:scale-[1.03]">
              <Package className="h-4 w-4" /> Consultar disponibilidade
            </a>
          </Reveal>
        </div>
        <div className="no-scrollbar -mt-4 flex gap-4 overflow-x-auto pb-4 sm:gap-6" data-testid="ready-stock-list">
          {readyStock.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} compact />
          ))}
        </div>
      </section>

      {/* ENCOMENDAS */}
      <section id="encomendas" data-testid="orders-section" className="relative overflow-hidden border-y border-white/10 py-24 sm:py-32">
        <img src={site.images.encomendasBg} alt="Arara de roupas premium para encomenda DM Imports" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/70 to-obsidian" />
        <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Reveal>
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-silver">Encomendas</p>
            <h2 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
              Não encontrou o modelo que queria?
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-zinc-400 sm:text-base">
              Nós também trabalhamos com encomendas. Fale com nossa equipe e consulte a disponibilidade do modelo que você procura.
            </p>
            <a href={waEncomenda()} target="_blank" rel="noopener noreferrer" data-testid="orders-whatsapp-button" className="mt-9 inline-flex items-center gap-3 rounded-full bg-offwhite px-9 py-4 text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-transform duration-300 hover:scale-[1.04]">
              <MessageCircle className="h-4 w-4" /> Fazer encomenda
            </a>
          </Reveal>
        </div>
      </section>

      {/* PROVA SOCIAL */}
      <section id="clientes" data-testid="social-proof-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="flex flex-col gap-12">
          <Reveal className="max-w-xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-zinc-500">Prova social</p>
            <h2 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
              Mais de 1.000 clientes atendidos
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-zinc-400 sm:text-base">
              A DM Imports nasceu para trazer produtos diferenciados e atendimento próximo para quem valoriza estilo e qualidade.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              { value: <CountUp to={1000} />, label: "Clientes atendidos", id: "stat-clientes" },
              { value: "Importados", label: "e Nacionais", id: "stat-importados" },
              { value: "Pronta Entrega", label: "e Encomendas", id: "stat-entrega" },
            ].map((s, i) => (
              <Reveal key={s.id} delay={i * 0.1} className="rounded-2xl border border-white/10 bg-coal p-7">
                <p data-testid={s.id} className="font-display text-xl font-extrabold uppercase leading-tight tracking-tight text-offwhite sm:text-2xl">{s.value}</p>
                <p className="mt-1.5 text-[11px] uppercase tracking-[0.25em] text-zinc-500">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section id="instagram" data-testid="instagram-section" className="border-y border-white/10 bg-graphite py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
            <ChapterHeading num="06" eyebrow="Instagram" title="Acompanhe a DM Imports" sub="Novos modelos, reposições e novidades todos os dias no nosso Instagram." testid="instagram-heading" />
            <Reveal className="shrink-0 md:pb-16">
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="instagram-follow-button" className="inline-flex items-center gap-3 rounded-full bg-offwhite px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-transform duration-300 hover:scale-[1.03]">
                <Instagram className="h-4 w-4" /> Seguir {INSTAGRAM_HANDLE}
              </a>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-6" data-testid="instagram-grid">
            {site.instagram.map((img, i) => (
              <Reveal key={img} delay={i * 0.06}>
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid={`instagram-post-${i + 1}`} className="group relative block overflow-hidden rounded-xl border border-white/10">
                  <img src={img} alt={`Post ${i + 1} do Instagram da DM Imports`} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 flex items-center justify-center bg-obsidian/60 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <Instagram className="h-7 w-7 text-offwhite" />
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMO COMPRAR */}
      <section id="como-comprar" data-testid="how-to-buy-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <ChapterHeading num="07" eyebrow="Sem carrinho, sem complicação" title="Comprar é fácil" testid="how-to-buy-heading" />
        <div className="grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step, i) => (
            <Reveal key={step.title} delay={i * 0.1} className="group relative bg-obsidian p-8 transition-colors duration-500 hover:bg-coal" data-testid={`how-to-step-${i + 1}`}>
              <span className="text-stroke-faint font-display text-6xl font-extrabold leading-none">0{i + 1}</span>
              <step.icon className="mt-8 h-6 w-6 text-silver" />
              <h3 className="mt-4 font-display text-lg font-bold uppercase tracking-tight text-offwhite">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">{step.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
};

export default Home;
